<script setup lang="ts">
// A shop's logo when it has one (src), else initials on a soft tone picked from the id, so a
// shop or person keeps its tone everywhere. photo: the picture fills the circle (a product's
// picture); otherwise the whole logo shows on white.
const props = defineProps<{ name: string; id?: string; size?: number; brand?: boolean; src?: string | null; photo?: boolean }>()

const HUES = [140, 200, 25, 265, 170, 45, 220]
const failed = ref(false)
watch(
  () => props.src,
  () => (failed.value = false),
)

const initials = computed(
  () =>
    props.name
      .split(/[\s@.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]!.toUpperCase())
      .join("") || "?",
)

const hue = computed(() => {
  let h = 0
  for (const c of props.id ?? props.name) h = (h * 31 + c.charCodeAt(0)) >>> 0
  return HUES[h % HUES.length]
})

const box = computed(() => ({ width: `${props.size ?? 40}px`, height: `${props.size ?? 40}px` }))
</script>

<template>
  <img v-if="src && !failed" class="avatar picture" :class="{ photo }" :src="src" :style="box" alt="" @error="failed = true" />
  <span
    v-else
    class="avatar"
    :class="{ brand }"
    :style="{ ...box, '--hue': hue, fontSize: `${Math.round((size ?? 40) * 0.36)}px` }"
    aria-hidden="true"
    >{{ initials }}</span
  >
</template>

<style scoped>
.avatar {
  flex: none;
  border-radius: 50%;
  display: inline-grid;
  place-items: center;
  background: hsl(var(--hue) 22% 88%);
  color: hsl(var(--hue) 35% 22%);
  font-weight: 600;
  letter-spacing: 0.02em;
}

@media (prefers-color-scheme: dark) {
  .hub-ui:not(.hub-theme-pos) .avatar:not(.picture) {
    background: hsl(var(--hue) 16% 26%);
    color: hsl(var(--hue) 30% 86%);
  }
}

.avatar.brand {
  background: var(--hub-brand);
  color: var(--hub-on-brand);
}

/* A logo shows whole, on white (logos are drawn for a light page) */
.picture {
  object-fit: contain;
  background: #ffffff;
  border: 1px solid var(--hub-border);
  padding: 3px;
}

.picture.photo {
  object-fit: cover;
  padding: 0;
  border: 0;
}

.hub-theme-pos .avatar {
  border-radius: 2px;
}
</style>
